import React, { useState } from 'react';
import styled              from 'styled-components';
import THead               from './components/THead';
import TRow                from './components/TRow';
import { CustomScrollBar } from '../UI/CustomScrollBar';
import { apiTypes }        from 'shared/consts/handbooksApiTypes';

interface IProps {
  config: any;
  data: {
    list: any[];
    count: number;
  };
  sort: {
    field: string;
    direction: string;
  };
  connections: {
    [key: string]: any[]
  };
  externalFilters?: any;
  onSortChange?: (field: string, direction: string) => void;
  onFilterChange?: (filters: string | { [x: string]: any[]; }[]) => void;
  onSelectedChange?: (selectedRows: any[], currentSelectedRow: any) => void;
  disableSelection?: boolean;
  multipleConfig?: boolean;
};
interface IConditionResponse {
  conditionResult: boolean;
  resultRow: any;
};

const SpreadTable = styled.table`
  width: 100%;
  border: 1px solid #ddd;
  border-spacing: 0;
  font-size: 12px;
  td{
    background:#fff;
  }
`;

const ScrollWrapper = styled.div<{ tabsExist?: boolean }>`
  position: relative;
  height: calc(100vh - 66px - 38px - 32px - ${({ theme }) => theme.layout.navTopPanelHeight}px - ${({ tabsExist }) => tabsExist ? 42 : 0}px);
`

const SpreadSheet = ({
  config,
  data,
  sort,
  connections,
  externalFilters,
  onSortChange,
  onFilterChange,
  onSelectedChange,
  disableSelection,
  multipleConfig
}: IProps) => {
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [currentSelectedRow, setCurrentSelectedRow] = useState(null);

  const getIndexValue = (config: any, row: any, index: number): string => {
    const indexField = config.fields.find((fl: any) => fl.index);
    if (indexField) {
      return row[indexField.name] || `new-${index}`;
    }
    return '';
  };

  const onSelectRow = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: any
  ) => {
    if (disableSelection) {
      return;
    };

    let result = { selectedRows: selectedRows, currentSelectedRow: currentSelectedRow };
    const list = (data && data.list) || [];

    if (e.ctrlKey) {
      if (selectedRows.indexOf(row) >= 0) {
        result = {
          selectedRows: selectedRows.filter((el) => el !== row),
          currentSelectedRow: null,
        };
      } else {
        result = {
          selectedRows: [...selectedRows, row],
          currentSelectedRow: row,
        };
      }
    } else if (e.shiftKey) {
      const rowIndex = list.indexOf(row);
      if (rowIndex >= 0) {
        let currentSelIndex: number =
          (currentSelectedRow && list.indexOf(currentSelectedRow)) || -1;
        if (currentSelIndex < 0) {
          // поиск ближайшего выделенного элемента
          let i = 1;
          let prev, next;
          while (true) {
            if (rowIndex - i >= 0) {
              prev = list[rowIndex - i];
            } else prev = null;
            if (rowIndex + i < list.length) {
              next = list[rowIndex + i];
            } else next = null;
            if (prev !== null && selectedRows.indexOf(prev) >= 0) {
              currentSelIndex = rowIndex - i;
              break;
            }
            if (next !== null && selectedRows.indexOf(next) >= 0) {
              currentSelIndex = rowIndex + i;
              break;
            }
            if (rowIndex - i < 0 && rowIndex + i >= list.length) {
              break;
            }
            ++i;
          }
        }
        if (currentSelIndex < 0) {
          currentSelIndex = 0;
        }
        const startIndex =
          rowIndex < currentSelIndex ? rowIndex : currentSelIndex;
        const endIndex =
          rowIndex > currentSelIndex ? rowIndex : currentSelIndex;
        result = {
          selectedRows: list.slice(startIndex, endIndex + 1),
          currentSelectedRow: row,
        };
      }
    } else {
      if (selectedRows.indexOf(row) < 0) {
        result = { selectedRows: [row], currentSelectedRow: row };
      }
    }
    setCurrentSelectedRow(result.currentSelectedRow);
    setSelectedRows(result.selectedRows)
    onSelectedChange &&
      onSelectedChange(
        result.selectedRows || [],
        result.currentSelectedRow
      );
  };

  const dependensCondition = (row: any, fieldName: any, condition: any): string => {
    let conditionResult: string;

    switch(condition){
        case 'is-not-empty':
          conditionResult = row[fieldName] !== null ? String(row[fieldName]).length > 0 ? '1' : '0' : '0';
          break;
        default:
          conditionResult = '1';
    }

    return conditionResult;
  };

  const checkConditions = (oldCurrentSelectedRow: any, field: any, value: any): IConditionResponse => {
    let conditionValue: any;
    let conditionViceVersaValue: any;
    let conditionMemo: string = '';

    if(field.conditions && Array.isArray(field.conditions) && field.conditions.length > 0){
      field.conditions.forEach((condition: any) => {
        if(condition.dependens && Array.isArray(condition.dependens) && condition.dependens.length > 0){
          conditionValue = condition.value === 'inherited' ? value : condition.value;
          conditionViceVersaValue = condition.viceVersaValue;
          condition.dependens.forEach((depend: any) => {
            conditionMemo += dependensCondition(oldCurrentSelectedRow, depend.name, depend.condition);
          });
        }
      });
    };


    const currentSelectedRow: any = {...oldCurrentSelectedRow, [field.name]: conditionMemo.includes('0') ? conditionViceVersaValue : conditionValue};

    return {
      conditionResult: true,
      resultRow: currentSelectedRow
    };
  };

  const onRowChange = (row: any, field: any, value: any) => {
    const rows = selectedRows ? [...selectedRows] : [];
    const conditionResponse = checkConditions(row, field, value);
    const conditionsResult = field.conditions ? conditionResponse['conditionResult'] : true;
    const resultRow = field.conditions ? conditionResponse['resultRow'] : row;
    const indexField: string = config.fields.find((field: any) => field.index)?.name || 'id';

    if(conditionsResult){
      if (rows.indexOf(resultRow) < 0) {
        rows.push(resultRow);
      }

      if (field.type === 'boolean' && field.unique) {
        data.list.forEach(_row => {
          if (_row[indexField] !== row[indexField] && _row[field.name]) {
            _row[field.name] = false;
            _row['__hasChanges'] = true;
            _row['__lastmodified'] = new Date();
          }
        })
      }

      rows.forEach((r: any) => {
        if (field.unique && r[indexField] !== row[indexField]) {
          return;
        }
        if (field.name === 'dow' && config.apiType !== apiTypes.DEFAULT) {
          // @TODO пересмотреть выполнение этой строки
          r[field.name] = r[field.name]?.includes(value) ? r[field.name]?.split('').filter((val: any) => val !== value.toString()).join('') : [...r[field.name], value.toString()].join('');
        } else {
          if (config.convertFunctions?.hasOwnProperty(field.name)) {
            r[field.name] = config.convertFunctions[field.name](value);
          } else if (field.multipleSelection) {
            r[field.name] = value;
          } else {
            r[field.name] = field.conditions ? resultRow[field.name] : field.id_type && field.id_type === 'number' ? +value : value;
          }

          if (field.hasRelatedFields) {
            config.fields.filter((f: any) => f.relatedField).forEach((relF: any) => {
              r[relF.name] = connections?.[relF.connection]?.find((con: any) => con.id === value)?.[relF.name]
            })
          }
        }
        
        if(r['__errors']){
          r['__errors'][field.name] = undefined;
        }
        r['__hasChanges'] = true;
        r['__lastmodified'] = new Date();
      });
      
      onSelectedChange &&
        onSelectedChange(
          selectedRows || [],
          currentSelectedRow
        );
    }
  };

  const onRowIndexChange = (row: any, field: any, value: any) => {
    const rows = selectedRows ? [...selectedRows] : [];
    if (rows.indexOf(row) < 0) {
      rows.push(row);
    }
    rows.forEach((r: any) => {
      r['__index__'+field.name] = value;
      if(r['__errors']){
        r['__errors'][field.name]=undefined;
      }
      r['__hasChanges'] = true;
      r['__lastmodified'] = new Date();
    });
    onSelectedChange &&
      onSelectedChange(
        selectedRows || [],
        currentSelectedRow
      );
  };

  return (
    <ScrollWrapper tabsExist={multipleConfig}>
      <CustomScrollBar >
          <SpreadTable>
            {
              config.fields.map((field: any) => {
                if (field.innerColumns) {
                  return <colgroup span={field.innerColumns.length}></colgroup>;
                }
                
                return <col></col>;
              })
            }
            {config && (
                <THead
                  config={config}
                  sort={sort}
                  onSortChange={onSortChange}
                  onFilterChange={onFilterChange}
                  connections={connections}
                  externalFilters={externalFilters}
                />
            )}
            <tbody>
              {
                data?.list ? data.list.map((row: any, index: number) => {
                  return (
                    <TRow
                      key={`row-${getIndexValue(config, row, index)}`}
                      indexRow={getIndexValue(config, row, index)}
                      config={config}
                      row={row}
                      onSelectRow={onSelectRow}
                      isSelected={selectedRows.indexOf(row) >= 0}
                      onRowChange={onRowChange}
                      onRowIndexChange={onRowIndexChange}
                      errors={row['__errors']}
                      lastmodified={row['__lastmodified']}
                      connections={connections}
                    />
                  );
                }) : <></>
              }
            </tbody>
          </SpreadTable>
      </CustomScrollBar>
    </ScrollWrapper>
  );
};

export default SpreadSheet;